import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Interceptors
api.interceptors.request.use(
  (config) => {
    // Adicionar timestamp para evitar cache
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }

    // Garantir token JWT em toda requisição (defesa contra refresh de página)
    // Rotas do portal de famílias usam o token do pai; demais rotas usam o token da cantina
    if (!config.headers.Authorization) {
      const isPortalRoute = config.url && config.url.startsWith('/api/familia')
      const familiaToken = localStorage.getItem('familia_token')
      const cantinaToken = localStorage.getItem('cantina_token')
      const token = isPortalRoute ? familiaToken : (cantinaToken || familiaToken)
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.code === 'ERR_NETWORK') {
      console.error('Erro de conexão. Verifique se o servidor está online.')
    }

    if (error.response) {
      console.error('Erro API:', error.response.data)

      // Token expirado ou inválido — redireciona para login
      if (error.response.status === 401) {
        const isPortal = window.location.pathname.startsWith('/portal')
        if (isPortal) {
          localStorage.removeItem('familia_token')
          localStorage.removeItem('familia_user')
        } else {
          localStorage.removeItem('cantina_token')
          localStorage.removeItem('cantina_user')
        }
        delete api.defaults.headers.common['Authorization']
        const redirectTo = isPortal ? '/portal' : '/login'
        if (window.location.pathname !== redirectTo) {
          window.location.href = redirectTo
        }
      }
    }

    return Promise.reject(error)
  }
)

// ============ CLIENTES ============
export const clienteService = {
  // Listar todos
  listar: (params = {}) => api.get('/api/clientes', { params }),
  
  // Buscar por ID
  buscar: (id) => api.get(`/api/clientes/${id}`),
  
  // Busca rápida
  buscarRapido: (termo) => api.get('/api/clientes/buscar', { params: { q: termo } }),
  
  // Criar
  criar: (data) => api.post('/api/clientes', data),
  
  // Atualizar
  atualizar: (id, data) => api.put(`/api/clientes/${id}`, data),
  
  // Deletar
  deletar: (id) => api.delete(`/api/clientes/${id}`),
  
  // Listar turmas
  turmas: () => api.get('/api/clientes/turmas'),
  
  // Extrato
  extrato: (id, params = {}) => api.get(`/api/clientes/${id}/extrato`, { params }),
  
  // Link WhatsApp cobrança
  linkCobranca: (id) => api.get(`/api/clientes/${id}/cobranca-whatsapp`),

  // Depositar saldo
  depositar: (id, data) => api.post(`/api/clientes/${id}/depositar`, data),

  // Restrições de produtos
  listarRestricoes: (id) => api.get(`/api/clientes/${id}/restricoes-produtos`),
  adicionarRestricoes: (id, produtoIds) => api.post(`/api/clientes/${id}/restricoes-produtos`, { produto_ids: produtoIds }),
  removerRestricao: (id, produtoId) => api.delete(`/api/clientes/${id}/restricoes-produtos/${produtoId}`)
}

// ============ PRODUTOS ============
export const produtoService = {
  // Listar todos
  listar: (params = {}) => api.get('/api/produtos', { params }),
  
  // Buscar por ID
  buscar: (id) => api.get(`/api/produtos/${id}`),
  
  // Busca rápida
  buscarRapido: (termo, venda = true) => api.get('/api/produtos/buscar', { 
    params: { q: termo, venda: venda ? 1 : 0 } 
  }),
  
  // Criar
  criar: (data) => api.post('/api/produtos', data),
  
  // Atualizar
  atualizar: (id, data) => api.put(`/api/produtos/${id}`, data),
  
  // Deletar
  deletar: (id) => api.delete(`/api/produtos/${id}`),
  
  // Listar categorias
  categorias: () => api.get('/api/produtos/categorias'),
  
  // Alertas estoque
  alertasEstoque: () => api.get('/api/produtos/alertas/estoque')
}

// ============ VENDAS ============
export const vendaService = {
  // Listar todas
  listar: (params = {}) => api.get('/api/vendas', { params }),
  
  // Buscar por ID
  buscar: (id) => api.get(`/api/vendas/${id}`),
  
  // Criar
  criar: (data) => api.post('/api/vendas', data),
  
  // Cancelar
  cancelar: (id, motivo = null) => api.post(`/api/vendas/${id}/cancelar`, { motivo }),
  
  // Resumo do dia
  resumoDia: (data = null) => api.get('/api/vendas/resumo/dia', { params: data ? { data } : {} }),
  
  // Sincronizar vendas offline
  sincronizar: (vendas) => api.post('/api/vendas/sync', { vendas })
}

// ============ PAGAMENTOS ============
export const pagamentoService = {
  // Listar todos
  listar: (params = {}) => api.get('/api/pagamentos', { params }),
  
  // Buscar por ID
  buscar: (id) => api.get(`/api/pagamentos/${id}`),
  
  // Criar pagamento
  processar: (data) => api.post('/api/pagamentos', data),
  
  // Estornar
  estornar: (id, motivo = null) => api.post(`/api/pagamentos/${id}/estornar`, { motivo })
}

// ============ DASHBOARD ============
export const dashboardService = {
  // Dashboard completo (legado)
  resumo: (data = null) => api.get('/api/dashboard', { params: data ? { data } : {} }),
  
  // Dashboard Inteligente - Novo endpoint completo
  inteligente: () => api.get('/api/dashboard/inteligente'),
  
  // Faturamento em tempo real
  faturamentoTempoReal: () => api.get('/api/dashboard/faturamento-tempo-real'),
  
  // Preparo do próximo recreio
  preparoRecreio: () => api.get('/api/dashboard/preparo-recreio'),
  
  // Alertas inteligentes
  alertas: () => api.get('/api/dashboard/alertas'),
  
  // Mapa de calor de filas
  mapaCalor: () => api.get('/api/dashboard/mapa-calor'),
  
  // Previsão de demanda
  previsaoDemanda: (produtoId = null, dias = 7) => api.get('/api/dashboard/previsao-demanda', { 
    params: produtoId ? { produto_id: produtoId, dias } : { dias } 
  }),
  
  // Sugestão de produção
  sugestaoProducao: (produtoId, turno = null) => api.get('/api/dashboard/sugestao-producao', { 
    params: { produto_id: produtoId, turno } 
  }),
  
  // Alertas não lidos
  alertasNaoLidos: () => api.get('/api/dashboard/alertas-nao-lidos'),
  
  // Alertas críticos
  alertasCriticos: () => api.get('/api/dashboard/alertas-criticos'),
  
  // Marcar alerta como lido
  marcarAlertaLido: (id) => api.post(`/api/dashboard/alertas/${id}/lido`),
  
  // Marcar todos os alertas como lidos
  marcarTodosAlertasLidos: () => api.post('/api/dashboard/alertas/marcar-todos-lidos'),
  
  // Conformidade nutricional
  conformidadeNutricional: (produtoId) => api.get('/api/dashboard/conformidade-nutricional', { 
    params: { produto_id: produtoId } 
  }),
  
  // Verificar alérgenos
  verificarAlergenos: (produtoId, clienteId = null) => api.get('/api/dashboard/verificar-alergenos', { 
    params: { produto_id: produtoId, cliente_id: clienteId } 
  }),
  
  // Resumo nutricional do cliente
  resumoNutricionalCliente: (clienteId, dias = 7) => api.get('/api/dashboard/resumo-nutricional-cliente', { 
    params: { cliente_id: clienteId, dias } 
  }),
  
  // Contas a receber (legado)
  contasReceber: (params = {}) => api.get('/api/dashboard/contas-receber', { params }),
  
  // Top devedores (legado)
  topDevedores: (limite = 10) => api.get('/api/dashboard/top-devedores', { params: { limite } }),
  
  // Aging report (legado)
  agingReport: () => api.get('/api/dashboard/aging-report')
}

// Verificar se API está online
export const checkApiStatus = async () => {
  try {
    await api.get('/', { timeout: 3000 })
    return true
  } catch (error) {
    return false
  }
}

export default api
